import { alpha, createTheme } from '@offline-mui/material/styles'
import type {} from '@offline-mui/x-date-pickers/themeAugmentation'

/* ------------------------------------------------------------------ */
/* Design tokens                                                       */
/* ------------------------------------------------------------------ */

export const FONT_SANS =
  '"Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
export const FONT_MONO =
  '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

export const slate = {
  50: '#F8FAFC',
  100: '#F1F5F9',
  200: '#E2E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  800: '#1E293B',
  900: '#0F172A',
  950: '#020617',
} as const

export const sidebar = {
  bg: '#0B1220',
  bgEnd: '#0F1A30',
  text: '#94A3B8',
  textMuted: '#5B6B84',
  textActive: '#FFFFFF',
  hover: 'rgba(255, 255, 255, 0.05)',
  active: 'rgba(96, 165, 250, 0.14)',
  border: 'rgba(255, 255, 255, 0.08)',
} as const

const primary = {
  main: '#2563EB',
  light: '#60A5FA',
  dark: '#1D4ED8',
  contrastText: '#FFFFFF',
}
const secondary = { main: '#0D9488', light: '#2DD4BF', dark: '#0F766E' }
const success = { main: '#16A34A', light: '#4ADE80', dark: '#15803D' }
const warning = { main: '#D97706', light: '#FBBF24', dark: '#B45309' }
const error = { main: '#DC2626', light: '#F87171', dark: '#B91C1C' }
const info = { main: '#0284C7', light: '#38BDF8', dark: '#0369A1' }

export const cardShadow =
  '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)'
export const popShadow =
  '0 12px 32px -8px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.08)'
export const focusRing = (color: string) => `0 0 0 3px ${alpha(color, 0.16)}`

const soft = (color: { main: string; dark: string }) => ({
  backgroundColor: alpha(color.main, 0.12),
  color: color.dark,
  '& .MuiChip-deleteIcon': {
    color: alpha(color.dark, 0.55),
    '&:hover': { color: color.dark },
  },
  '& .MuiChip-icon': { color: color.dark },
})

/* ------------------------------------------------------------------ */
/* Theme                                                               */
/* ------------------------------------------------------------------ */

const theme = createTheme({
  palette: {
    mode: 'light',
    primary,
    secondary,
    success,
    warning,
    error,
    info,
    background: {
      default: '#F5F7FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: slate[900],
      secondary: slate[500],
      disabled: slate[400],
    },
    divider: slate[200],
    action: {
      hover: alpha(slate[900], 0.04),
      selected: alpha(primary.main, 0.08),
      focus: alpha(primary.main, 0.12),
    },
    grey: slate,
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: FONT_SANS,
    h1: { fontSize: 36, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15 },
    h2: { fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h3: { fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25 },
    h4: { fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25 },
    h5: { fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h6: { fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.4 },
    subtitle1: { fontSize: 15, fontWeight: 600, lineHeight: 1.5 },
    subtitle2: { fontSize: 13.5, fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: 14, lineHeight: 1.6 },
    body2: { fontSize: 13, lineHeight: 1.55 },
    caption: { fontSize: 12, lineHeight: 1.5 },
    overline: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.08em',
      lineHeight: 1.5,
      textTransform: 'uppercase',
    },
    button: { fontSize: 14, fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F5F7FA',
        },
        code: {
          fontFamily: FONT_MONO,
          fontSize: '0.9em',
          backgroundColor: slate[100],
          padding: '1px 5px',
          borderRadius: 5,
        },
      },
    },

    /* Surfaces ------------------------------------------------------- */
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${slate[200]}`,
          borderRadius: 14,
        },
        elevation0: { boxShadow: cardShadow },
        elevation1: { boxShadow: cardShadow },
        elevation2: { boxShadow: popShadow },
        outlined: { boxShadow: 'none', borderColor: slate[200] },
      },
    },
    MuiCard: {
      styleOverrides: { root: { borderRadius: 14 } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: slate[200] } },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { border: 0, borderRadius: 0, boxShadow: 'none' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { borderRadius: 0, boxShadow: 'none' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, boxShadow: popShadow },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { borderRadius: 12, boxShadow: popShadow, border: `1px solid ${slate[200]}` },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 12, boxShadow: popShadow, marginTop: 4 },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: 14,
          minHeight: 36,
          '&.Mui-selected': {
            backgroundColor: alpha(primary.main, 0.08),
            '&:hover': { backgroundColor: alpha(primary.main, 0.12) },
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: slate[900],
          fontSize: 12,
          fontWeight: 500,
          borderRadius: 8,
          padding: '6px 10px',
        },
        arrow: { color: slate[900] },
      },
    },

    /* Buttons -------------------------------------------------------- */
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'background-color .15s ease, box-shadow .15s ease, border-color .15s ease, transform .15s ease',
          '&:active': { transform: 'translateY(0.5px)' },
        },
        sizeMedium: { padding: '9px 16px', minHeight: 42 },
        sizeSmall: { padding: '5px 12px', minHeight: 32, fontSize: 13 },
        sizeLarge: { padding: '12px 22px', minHeight: 48, fontSize: 15 },
        text: {
          '&:hover': { backgroundColor: alpha(primary.main, 0.06) },
        },
        startIcon: { marginRight: 6 },
        endIcon: { marginLeft: 6 },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            background: `linear-gradient(180deg, ${primary.main} 0%, ${primary.dark} 100%)`,
            boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.14)}, 0 1px 2px ${alpha(primary.dark, 0.3)}`,
            '&:hover': {
              background: `linear-gradient(180deg, ${primary.dark} 0%, ${primary.dark} 100%)`,
              boxShadow: `0 4px 14px ${alpha(primary.main, 0.32)}`,
            },
            '&.Mui-disabled': {
              background: slate[200],
              color: slate[400],
              boxShadow: 'none',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            borderColor: slate[300],
            color: slate[700],
            backgroundColor: '#FFFFFF',
            '&:hover': { backgroundColor: slate[50], borderColor: slate[400] },
          },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: slate[500],
          '&:hover': { backgroundColor: slate[100], color: slate[900] },
        },
        sizeSmall: { padding: 6 },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: slate[100],
          padding: 3,
          borderRadius: 10,
          gap: 2,
        },
        grouped: {
          border: 0,
          borderRadius: 8,
          '&:not(:first-of-type)': { borderRadius: 8, marginLeft: 0 },
          '&:not(:last-of-type)': { borderRadius: 8 },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          color: slate[600],
          border: 0,
          padding: '7px 16px',
          transition: 'all .15s ease',
          '&:hover': { backgroundColor: alpha(slate[900], 0.04), color: slate[900] },
          '&.Mui-selected': {
            backgroundColor: '#FFFFFF',
            color: primary.dark,
            boxShadow: cardShadow,
            '&:hover': { backgroundColor: '#FFFFFF' },
          },
        },
      },
    },

    /* Inputs --------------------------------------------------------- */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          fontSize: 14,
          transition: 'box-shadow .15s ease, border-color .15s ease',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: slate[400] },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: slate[500] },
          '&.Mui-focused': { boxShadow: focusRing(primary.main) },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1,
            borderColor: primary.main,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: error.main },
          '&.Mui-error.Mui-focused': { boxShadow: focusRing(error.main) },
          '&.Mui-disabled': { backgroundColor: slate[50] },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: slate[300] },
        },
        notchedOutline: {
          borderStyle: 'solid',
          borderWidth: 1,
          transition: 'border-color .15s ease',
          /* Bootstrap reboot sets fieldset { border: 0 } and legend margins.
             Leave legend width alone so the notch stays collapsed until the label floats. */
          '& legend': {
            margin: 0,
            lineHeight: '11px',
          },
        },
        input: {
          padding: '12.5px 14px',
          '&::placeholder': { color: slate[400], opacity: 1 },
        },
        multiline: { padding: '12.5px 14px', '& .MuiOutlinedInput-input': { padding: 0 } },
      },
      variants: [
        {
          props: { size: 'small' },
          style: {
            '& .MuiOutlinedInput-input': { padding: '8.5px 12px' },
            '&.MuiInputBase-multiline': { padding: '8.5px 12px' },
          },
        },
      ],
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          marginBottom: 0,
          fontSize: 14,
          fontWeight: 500,
          color: slate[500],
          '&.Mui-focused': { color: primary.main },
        },
        outlined: {
          transform: 'translate(14px, 12px) scale(1)',
          '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.78)' },
          '&.MuiInputLabel-sizeSmall': { transform: 'translate(12px, 9px) scale(1)' },
          '&.MuiInputLabel-sizeSmall.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.78)',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginLeft: 2, marginTop: 5, fontSize: 12 },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: { color: slate[500] },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          '&.MuiOutlinedInput-root': {
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: 9,
          },
          '&.MuiOutlinedInput-root .MuiAutocomplete-input': {
            padding: '7.5px 4px 7.5px 5px',
          },
        },
        paper: {
          borderRadius: 12,
          boxShadow: popShadow,
          border: `1px solid ${slate[200]}`,
          marginTop: 4,
        },
        listbox: { padding: 6 },
        option: {
          borderRadius: 8,
          fontSize: 14,
          minHeight: 36,
          '&.Mui-focused': { backgroundColor: `${slate[100]} !important` },
          '&[aria-selected="true"]': {
            backgroundColor: `${alpha(primary.main, 0.08)} !important`,
            fontWeight: 600,
          },
        },
        tag: { margin: 2, height: 26 },
        popupIndicator: { color: slate[500] },
        clearIndicator: { color: slate[500] },
      },
    },
    /* Date pickers use their own input primitives; mirror the OutlinedInput look. */
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          fontSize: 14,
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'box-shadow .15s ease, border-color .15s ease',
          '& .MuiPickersOutlinedInput-notchedOutline': { borderColor: slate[400] },
          '&:hover .MuiPickersOutlinedInput-notchedOutline': { borderColor: slate[500] },
          '&.Mui-focused': { boxShadow: focusRing(primary.main) },
          '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
            borderWidth: 1,
            borderColor: primary.main,
          },
          '&.Mui-error .MuiPickersOutlinedInput-notchedOutline': { borderColor: error.main },
          '&.Mui-error.Mui-focused': { boxShadow: focusRing(error.main) },
          '&.Mui-disabled': { backgroundColor: slate[50] },
          // Match the 48px / 40px heights of OutlinedInput medium / small.
          '& .MuiPickersInputBase-sectionsContainer': { padding: '12.5px 0' },
          '&.MuiPickersInputBase-inputSizeSmall .MuiPickersInputBase-sectionsContainer': {
            padding: '8.5px 0',
          },
        },
        notchedOutline: {
          borderStyle: 'solid',
          borderWidth: 1,
          transition: 'border-color .15s ease',
          '& legend': {
            margin: 0,
            lineHeight: '11px',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: { root: { color: slate[300] } },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: slate[300], borderRadius: 6 } },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { padding: 8 },
        track: { borderRadius: 999, backgroundColor: slate[300], opacity: 1 },
      },
    },

    /* Data display --------------------------------------------------- */
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: 12.5,
          height: 28,
          '& .MuiChip-label': { paddingLeft: 10, paddingRight: 10 },
        },
        sizeSmall: {
          height: 24,
          fontSize: 12,
          '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
        },
        filled: {
          backgroundColor: slate[100],
          color: slate[700],
        },
        outlined: {
          borderColor: slate[300],
          color: slate[700],
          backgroundColor: '#FFFFFF',
        },
        icon: { fontSize: 15, marginLeft: 8 },
      },
      variants: [
        { props: { variant: 'filled', color: 'primary' }, style: soft(primary) },
        { props: { variant: 'filled', color: 'secondary' }, style: soft(secondary) },
        { props: { variant: 'filled', color: 'success' }, style: soft(success) },
        { props: { variant: 'filled', color: 'warning' }, style: soft(warning) },
        { props: { variant: 'filled', color: 'error' }, style: soft(error) },
        { props: { variant: 'filled', color: 'info' }, style: soft(info) },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: { borderColor: alpha(primary.main, 0.4), color: primary.dark, backgroundColor: alpha(primary.main, 0.04) },
        },
        {
          props: { variant: 'outlined', color: 'success' },
          style: { borderColor: alpha(success.main, 0.4), color: success.dark, backgroundColor: alpha(success.main, 0.04) },
        },
        {
          props: { variant: 'outlined', color: 'warning' },
          style: { borderColor: alpha(warning.main, 0.4), color: warning.dark, backgroundColor: alpha(warning.main, 0.04) },
        },
        {
          props: { variant: 'outlined', color: 'error' },
          style: { borderColor: alpha(error.main, 0.4), color: error.dark, backgroundColor: alpha(error.main, 0.04) },
        },
      ],
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontSize: 13.5,
          alignItems: 'flex-start',
          border: '1px solid transparent',
          padding: '8px 14px',
        },
        icon: { fontSize: 20, marginRight: 10, paddingTop: 8 },
        message: { fontWeight: 500, paddingTop: 8, paddingBottom: 8 },
      },
      variants: [
        {
          props: { severity: 'success', variant: 'standard' },
          style: { backgroundColor: alpha(success.main, 0.08), borderColor: alpha(success.main, 0.25), color: success.dark },
        },
        {
          props: { severity: 'error', variant: 'standard' },
          style: { backgroundColor: alpha(error.main, 0.07), borderColor: alpha(error.main, 0.25), color: error.dark },
        },
        {
          props: { severity: 'warning', variant: 'standard' },
          style: { backgroundColor: alpha(warning.main, 0.08), borderColor: alpha(warning.main, 0.3), color: warning.dark },
        },
        {
          props: { severity: 'info', variant: 'standard' },
          style: { backgroundColor: alpha(info.main, 0.07), borderColor: alpha(info.main, 0.25), color: info.dark },
        },
      ],
    },
    MuiAlertTitle: {
      styleOverrides: { root: { fontWeight: 700, marginBottom: 2 } },
    },
    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0 },
      styleOverrides: {
        root: {
          border: 0,
          boxShadow: 'none',
          borderRadius: 0,
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: slate[50] },
          '&.Mui-expanded': { backgroundColor: slate[50] },
        },
        expandIconWrapper: { color: slate[500] },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: { height: 2, borderRadius: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: 14,
          minHeight: 44,
          minWidth: 0,
          padding: '0 4px',
          marginRight: 24,
          color: slate[500],
          '&:hover': { color: slate[900] },
          '&.Mui-selected': { color: slate[900] },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: slate[200], fontSize: 13.5, padding: '12px 16px' },
        head: {
          backgroundColor: slate[50],
          color: slate[500],
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: slate[50] } },
        head: { '&:hover': { backgroundColor: slate[50] } },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { color: slate[500], fontSize: 13 },
        toolbar: { minHeight: 52, paddingLeft: 16 },
        selectLabel: { fontSize: 13 },
        displayedRows: { fontSize: 13 },
        select: { fontSize: 13, borderRadius: 6 },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: { '&.MuiListItem-divider': { borderColor: slate[200] } },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, height: 6, backgroundColor: alpha(primary.main, 0.14) },
        bar: { borderRadius: 999 },
      },
    },
    MuiSkeleton: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { fontWeight: 500 } },
    },
  },
} as any)

export default theme
